import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    FormControl,
    FormHelperText,
    FormLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Box, Typography, Paper, TextField, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Add, Edit } from '@mui/icons-material';
import { updateLead } from '../../redux/Slice/LeadSlice';
import { setLoading } from '../../redux/Slice/loaderSlice';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const AddFollowups = ({ data, setData }) => {
    const [isEditingFollowUp, setIsEditingFollowUp] = useState(false);
    const [editingFollowUpId, setEditingFollowUpId] = useState(null);
    const dispatch = useDispatch();
    const params = useParams();

    const validationSchema = Yup.object({
        follow_up_subject: Yup.string().required('Follow-up Subject is required'),
        follow_up_notes: Yup.string().required('Follow-up Notes are required'),
        follow_up_type: Yup.string().required('Follow-up Type is required'),
        next_followUp_date: Yup.date().required('Next Follow-up Date is required').nullable(),
    });

    const formik = useFormik({
        initialValues: {
            follow_up_subject: '',
            follow_up_notes: '',
            follow_up_type: '',
            next_followUp_date: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            const { follow_up_subject, follow_up_notes, follow_up_type, next_followUp_date } = values;
            const followUps = [...data.followUps];
    
            try {
                dispatch(setLoading(true)); 
                
                if (editingFollowUpId) {
                    // Edit existing follow-up
                    const updatedFollowUps = followUps.map(followUp =>
                        followUp._id === editingFollowUpId ? { ...followUp, ...values } : followUp
                    );
                    await dispatch(updateLead({ l_id: params.id, formData: { ...data, followUps: updatedFollowUps } }));
                } else {
                    // Add new follow-up
                    followUps.push(values);
                    await dispatch(updateLead({ l_id: params.id, formData: { ...data, followUps } }));
                }
                
                setIsEditingFollowUp(false);
            } catch (error) {
                console.error('Error updating lead:', error);
            } finally {
                dispatch(setLoading(false));
            }
        },
    });
    

    const handleAddFollowUp = () => {
        setEditingFollowUpId(null);
        formik.resetForm();
        setIsEditingFollowUp(true);
    };

    const handleEditFollowUp = (id) => {
        const followUp = data?.followUps.find((followUp) => followUp._id === id);
        if (followUp) {
            setEditingFollowUpId(id);
            formik.setValues({
                follow_up_subject: followUp.follow_up_subject || '',
                follow_up_notes: followUp.follow_up_notes || '',
                follow_up_type: followUp.follow_up_type || '',
                next_followUp_date: followUp.next_followUp_date ? dayjs(followUp.next_followUp_date) : null,
            });
            setIsEditingFollowUp(true);
        }
    };

    return (
        <Grid container spacing={2}>
            {/* Follow-Ups Section */}
            <Grid item xs={12}>
                  <Paper elevation={3} sx={{ p: 2, pb: 5 }}>
                    <Box flexDirection="row" justifyContent="space-between" alignItems="center" display="flex">
                        <Typography variant="h6">Follow-Ups</Typography>
                        <div>
                            <IconButton onClick={handleAddFollowUp}>
                                <Add />
                            </IconButton>
                        </div>
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Subject</TableCell>
                                    <TableCell>Notes</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Next Follow-Up</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data && data?.followUps?.map((followUp) => (
                                    <TableRow key={followUp._id}>
                                        <TableCell>{followUp.follow_up_subject}</TableCell>
                                        <TableCell>{followUp.follow_up_notes}</TableCell>
                                        <TableCell>{followUp.follow_up_type}</TableCell>
                                        <TableCell> {dayjs(followUp.next_followUp_date).format('DD MMM ddd YYYY : hh.mm A')}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => handleEditFollowUp(followUp._id)}>
                                                <Edit />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>

            {/* Dialog for Adding/Editing Follow-Up */}
            <Dialog open={isEditingFollowUp} onClose={() => setIsEditingFollowUp(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingFollowUpId ? "Edit Follow-Up" : "Add Follow-Up"}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <FormLabel>Follow-Up Type</FormLabel>
                        <Select
                            name="follow_up_type"
                            value={formik.values.follow_up_type}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            displayEmpty
                        >
                            <MenuItem value="" disabled>Follow-Up Type</MenuItem>
                            <MenuItem value="Email">Email</MenuItem>
                            <MenuItem value="Call">Call</MenuItem>
                            <MenuItem value="Meeting">Meeting</MenuItem>
                        </Select>
                        <FormHelperText error={Boolean(formik.errors.follow_up_type)}>
                            {formik.errors.follow_up_type}
                        </FormHelperText>
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <FormLabel>Follow-Up Subject</FormLabel>
                        <TextField
                            placeholder="Follow-Up Subject"
                            variant="outlined"
                            fullWidth
                            name="follow_up_subject"
                            value={formik.values.follow_up_subject}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.follow_up_subject && Boolean(formik.errors.follow_up_subject)}
                            helperText={formik.touched.follow_up_subject && formik.errors.follow_up_subject}
                        />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <FormLabel>Follow-Up Notes</FormLabel>
                        <TextField
                            placeholder="Follow-Up Notes"
                            variant="outlined"
                            fullWidth
                            name="follow_up_notes"
                            value={formik.values.follow_up_notes}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.follow_up_notes && Boolean(formik.errors.follow_up_notes)}
                            helperText={formik.touched.follow_up_notes && formik.errors.follow_up_notes}
                        />
                    </FormControl>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <FormLabel>Follow-Up Date</FormLabel>
                            <MobileDateTimePicker
                                format="LLL"
                                value={formik.values.next_followUp_date || null}
                                onChange={(newValue) => {
                                    const formattedDate = dayjs(newValue);
                                    formik.setFieldValue('next_followUp_date', formattedDate);
                                }}
                                onError={(reason) => {
                                    if (reason) {
                                        formik.setFieldError('next_followUp_date', "Invalid date");
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        fullWidth
                                        placeholder="Follow-Up Date"
                                        error={formik.touched.next_followUp_date && Boolean(formik.errors.next_followUp_date)}
                                        helperText={formik.touched.next_followUp_date && formik.errors.next_followUp_date}
                                    />
                                )}
                            />
                        </FormControl>
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsEditingFollowUp(false)} color="secondary">Cancel</Button>
                    <Button onClick={formik.handleSubmit} color="primary">{editingFollowUpId ? "Update" : "Add"} Follow-Up</Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default AddFollowups;
